<script lang="ts" setup>
import { RouteRedispatchState, RouteStatus, TravelOrderStatus, TravelOrderStatusMap } from "@/constants/trip";
import TripTimer from "@/views/trips/components/TripTimer.vue";
import type { TripMonitorOrder, TripMonitorRoute, TripMonitorTravelOrder } from "@/api/trips/dto/trip-monitor";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";

const { t } = useI18n();

const props = defineProps<{
  travelOrder: TripMonitorTravelOrder;
  index: number;
  route: TripMonitorRoute;
  tripId: number;
  orders: TripMonitorOrder[];
}>();

const emits = defineEmits<{
  (e: "updateMonitor"): void;
  (e: "showCreateDialog", travelOrder: TripMonitorTravelOrder): void;
  (e: "viewUpdate", updateId: number): void;
  (e: "openIntervalDialog", travelOrder: TripMonitorTravelOrder): void;
}>();

const isCancelled = computed(() => {
  return props.route.status === RouteStatus.cancelled;
});

const isPlanned = computed(() => {
  return props.route.status === RouteStatus.planned;
});

const isTravelOrderInProgress = computed(() => {
  const nextStopTravelOrderId = props.route.nextRouteStop?.travelOrderId;

  if (nextStopTravelOrderId) {
    return props.travelOrder.id === nextStopTravelOrderId;
  }

  const firstTravelOrderId = [...props.route.travelOrders].sort((a, b) => a.id - b.id)[0]?.id;

  return props.travelOrder.id === firstTravelOrderId;
});

const relatedOrder = computed(() => {
  return props.orders.find((order) => order.id === props.travelOrder.orderId);
});

const relatedCustomer = computed(() => {
  return relatedOrder.value?.customer;
});

const isPossibleSendUpdates = computed(() => {
  return relatedCustomer.value?.sendUpdates;
});

const buttonDisabled = computed(() => {
  if (!isTravelOrderInProgress.value && props.route.technicalPartial) {
    return true;
  }

  return (
    props.route.redispatch === RouteRedispatchState.needed ||
    isCancelled.value ||
    isPlanned.value ||
    !isPossibleSendUpdates.value
  );
});

const showTimer = computed(() => {
  if (props.route.technicalPartial) {
    return isTravelOrderInProgress.value;
  }

  return Boolean(isPossibleSendUpdates.value);
});

const brokerUpdateDispatched = computed(() => {
  if (!props.route?.lastCheckCall) {
    return true;
  }

  return buttonDisabled.value;
});

function getTravelOrderStatus(status: TravelOrderStatus) {
  return TravelOrderStatusMap.find((item) => item.id === status)?.name;
}
</script>

<template>
  <div class="d-flex align-center">
    <div class="font-size-12 text-grey-darken-2 mr-2" data-qa="travel-order-number">
      <span class="font-weight-500 mr-1">{{ t("general.short-travel-order") }} #{{ index + 1 }}</span>

      <span>({{ t("general.order") }}#{{ relatedOrder?.number }})</span>
    </div>

    <permission-tooltip v-if="showTimer" :permission="DispatcherPermissionValue.BrokerUpdatesEdit">
      <template #activator="{ disabled }">
        <trip-timer
          :disabled="buttonDisabled || disabled"
          :interval="travelOrder.updateInterval"
          :next-update-time="travelOrder.nextUpdateTime"
          :truck-id="route.truck.id"
          :truck-number="route.truck.number"
          class="mr-2"
          @open-dialog="emits('openIntervalDialog', travelOrder)"
        ></trip-timer>
      </template>

      {{ t("permission.create-broker-update") }}
    </permission-tooltip>

    <template v-if="travelOrder.status === TravelOrderStatus.active">
      <div v-if="travelOrder.lastUpdateId" class="d-flex align-center">
        <permission-tooltip :permission="DispatcherPermissionValue.BrokerUpdatesList">
          <template #activator="{ disabled }">
            <v-btn
              :disabled="disabled"
              color="primary"
              size="xsmall"
              variant="text"
              @click="emits('viewUpdate', travelOrder.lastUpdateId)"
            >
              <template #prepend>
                <v-icon color="primary" size="small">mdi-eye-outline</v-icon>
              </template>

              <template #append>
                <span class="text-primary font-size-11 font-weight-600 ml-1">View update</span>
              </template>
            </v-btn>
          </template>

          {{ t("permission.view-broker-update") }}
        </permission-tooltip>
      </div>

      <template v-else>
        <permission-tooltip :permission="DispatcherPermissionValue.BrokerUpdatesEdit">
          <template #activator="{ disabled }">
            <v-btn
              :disabled="brokerUpdateDispatched || disabled"
              class="font-size-11 font-weight-600"
              color="primary"
              data-qa="send-update-button"
              density="comfortable"
              size="small"
              variant="flat"
              @click="emits('showCreateDialog', travelOrder)"
            >
              Send update
            </v-btn>
          </template>

          {{ t("permission.create-broker-update") }}
        </permission-tooltip>
      </template>
    </template>

    <v-chip v-else class="font-size-11" color="red-lighten-2" density="comfortable" size="small" variant="flat">
      {{ getTravelOrderStatus(travelOrder.status) }}
    </v-chip>
  </div>
</template>

<style lang="scss" scoped></style>
